import React from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  Card, 
  CardContent,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import { 
  Assignment as TaskIcon,
  CheckCircle as CompletedIcon,
  Schedule as PendingIcon,
  PriorityHigh as HighPriorityIcon
} from '@mui/icons-material';

const UpazilaAdminTasks = () => {
  // Mock data for upazila admin tasks
  const tasks = [
    {
      id: 1,
      title: 'দোকান অনুমোদন পর্যালোচনা',
      description: 'নতুন দোকান অনুমোদনের জন্য আবেদন পর্যালোচনা করুন',
      priority: 'high',
      status: 'pending',
      assignedTo: 'আমি',
      dueDate: 'আজ',
      category: 'অনুমোদন'
    },
    {
      id: 2,
      title: 'সাপ্তাহিক রিপোর্ট প্রস্তুতি',
      description: 'উপজেলা সাপ্তাহিক কর্মকাণ্ডের রিপোর্ট প্রস্তুত করুন',
      priority: 'medium',
      status: 'in_progress',
      assignedTo: 'আমি',
      dueDate: 'শুক্রবার',
      category: 'রিপোর্টিং'
    },
    {
      id: 3,
      title: 'ব্যবহারকারী যাচাইকরণ',
      description: 'নতুন ব্যবহারকারীদের পরিচয় যাচাইকরণ করুন',
      priority: 'high',
      status: 'pending',
      assignedTo: 'আমি',
      dueDate: 'আগামীকাল',
      category: 'নিরাপত্তা'
    },
    {
      id: 4,
      title: 'দোকান পরিদর্শন',
      description: 'অনুমোদিত দোকানগুলির নিয়মিত পরিদর্শন করুন',
      priority: 'medium',
      status: 'completed',
      assignedTo: 'আমি',
      dueDate: 'গতকাল',
      category: 'পরিদর্শন'
    }
  ];

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'error';
      case 'medium': return 'warning';
      case 'low': return 'info';
      default: return 'default';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'success';
      case 'in_progress': return 'primary';
      case 'pending': return 'warning';
      default: return 'default';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'সম্পন্ন';
      case 'in_progress': return 'চলমান';
      case 'pending': return 'অপেক্ষমান';
      default: return 'অজানা';
    }
  };

  const getPriorityText = (priority) => {
    switch (priority) {
      case 'high': return 'উচ্চ';
      case 'medium': return 'মাঝারি';
      case 'low': return 'নিম্ন';
      default: return 'অজানা';
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 600, mb: 4 }}>
        উপজেলা প্রশাসক কাজসমূহ
      </Typography>

      {/* Task Statistics */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" component="div">
                {tasks.length}
              </Typography>
              <Typography variant="body2">
                মোট কাজ
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" component="div">
                {tasks.filter(t => t.status === 'pending').length}
              </Typography>
              <Typography variant="body2">
                অপেক্ষমান
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'info.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" component="div">
                {tasks.filter(t => t.status === 'in_progress').length}
              </Typography>
              <Typography variant="body2">
                চলমান
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
            <CardContent>
              <Typography variant="h4" component="div">
                {tasks.filter(t => t.status === 'completed').length}
              </Typography>
              <Typography variant="body2">
                সম্পন্ন
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Tasks Table */}
      <Paper sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 600 }}>
            কাজের তালিকা
          </Typography>
          <Button variant="contained" startIcon={<TaskIcon />}>
            নতুন কাজ যোগ করুন
          </Button>
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>কাজের নাম</TableCell>
                <TableCell>বিবরণ</TableCell>
                <TableCell>অগ্রাধিকার</TableCell>
                <TableCell>অবস্থা</TableCell>
                <TableCell>দায়িত্বপ্রাপ্ত</TableCell>
                <TableCell>সময়সীমা</TableCell>
                <TableCell>কার্যক্রম</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasks.map((task) => (
                <TableRow key={task.id}>
                  <TableCell>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {task.title}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {task.category}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {task.description}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getPriorityText(task.priority)}
                      color={getPriorityColor(task.priority)}
                      size="small"
                      icon={task.priority === 'high' ? <HighPriorityIcon /> : undefined}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={getStatusText(task.status)}
                      color={getStatusColor(task.status)}
                      size="small"
                      icon={
                        task.status === 'completed' ? <CompletedIcon /> :
                        task.status === 'pending' ? <PendingIcon /> : undefined
                      }
                    />
                  </TableCell>
                  <TableCell>{task.assignedTo}</TableCell>
                  <TableCell>{task.dueDate}</TableCell>
                  <TableCell>
                    <Button 
                      variant="outlined" 
                      size="small"
                      sx={{ mr: 1 }}
                    >
                      সম্পাদনা
                    </Button>
                    <Button 
                      variant="contained" 
                      size="small"
                      color="success"
                    >
                      সম্পন্ন
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default UpazilaAdminTasks;
